<template>
    <div>
        <v-btn
            :disabled="!filterKey"
            :color="filterKey ? 'primary' : 'grey'"
            variant="text"
            size="x-small"
            @click="showModal"
        >
            {{ $t('summary_of_reservations') }}
        </v-btn>
        <ModalWrapper v-model="modal" max-width="700">
            <template v-slot:title>
                {{ $t('summary_of_reservations') }}
            </template>
            <template v-slot:content>
                <v-tabs v-model="tab" class="mb-4">
                    <v-tab value="orders">{{ $t('orders') }}</v-tab>
                    <v-tab value="quotations">{{ $t('quotations') }}</v-tab>
                </v-tabs>
                <v-tabs-window v-model="tab">
                    <v-tabs-window-item value="orders">
                        <v-row dense>
                            <v-col cols="12" sm="5">
                                <div class="bg-grey-lighten-4 text-subtitle-1 font-weight-medium py-3 px-4 mb-2">
                                    {{ $t('suppliers', 2) }}
                                </div>
                                <v-data-table
                                    :headers="headers"
                                    :items="supplierSummaryItems"
                                    disable-sort
                                    hide-default-footer
                                >
                                    <template v-slot:header.dueToPayAmount="{column}">
                                        <div class="d-flex flex-column">
                                            <span>
                                                {{ column.title }}
                                            </span>
                                            <div
                                                class="d-flex text-primary font-weight-bold full-width justify-end text-subtitle-1 my-3"
                                            >
                                                <span>
                                                    {{
                                                        $filters.priceFormat(
                                                            supplierSummaryInCurrency.dueToPayAmount,
                                                            supplierSummaryInCurrency.currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:header.turnoverAmount="{column}">
                                        <div class="d-flex flex-column">
                                            <span>
                                                {{ column.title }}
                                            </span>
                                            <div
                                                class="d-flex text-primary font-weight-bold full-width justify-end text-subtitle-1 my-3"
                                            >
                                                <span class="text--primary">
                                                    {{
                                                        $filters.priceFormat(
                                                            supplierSummaryInCurrency.turnoverAmount,
                                                            supplierSummaryInCurrency.currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-col>
                            <v-col v-if="!isMobile" cols="1" />
                            <v-col cols="12" sm="6">
                                <div class="bg-grey-lighten-4 text-subtitle-1 font-weight-medium py-3 px-4 mb-2">
                                    {{ $t('clients') }}
                                </div>
                                <v-data-table
                                    :headers="headers"
                                    :items="clientSummaryItems"
                                    disable-sort
                                    hide-default-footer
                                >
                                    <template v-slot:item.turnoverAmount="{item}">
                                        {{ item.turnoverAmount }}
                                        <span v-if="item.turnoverWithouCommissionAmount">
                                            {{ `(${item.turnoverWithouCommissionAmount})` }}
                                        </span>
                                    </template>
                                    <template v-slot:header.dueToPayAmount="{column}">
                                        <div class="d-flex flex-column">
                                            <span>
                                                {{ column.title }}
                                            </span>
                                            <div
                                                class="d-flex text-primary font-weight-bold full-width justify-end text-subtitle-1 my-3"
                                            >
                                                <span>
                                                    {{
                                                        $filters.priceFormat(
                                                            clientSummaryInCurrency.dueToPayAmount,
                                                            clientSummaryInCurrency.currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:header.turnoverAmount="{column}">
                                        <div class="d-flex flex-column">
                                            <span>
                                                {{ column.title }}
                                            </span>
                                            <div
                                                class="d-flex text-primary font-weight-bold full-width justify-end text-subtitle-1 my-3"
                                            >
                                                <span class="text--primary">
                                                    {{
                                                        $filters.priceFormat(
                                                            clientSummaryInCurrency.turnoverAmount,
                                                            clientSummaryInCurrency.currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-tabs-window-item>
                    <v-tabs-window-item value="quotations">
                        <v-row dense>
                            <v-col cols="12" sm="5">
                                <div class="bg-grey-lighten-4 text-subtitle-1 font-weight-medium py-3 px-4 mb-2">
                                    {{ $t('suppliers', 2) }}
                                </div>
                                <v-data-table
                                    :headers="headers"
                                    :items="supplierSummaryQuotationsItems"
                                    disable-sort
                                    hide-default-footer
                                />
                            </v-col>
                            <v-col v-if="!isMobile" cols="1" />
                            <v-col cols="12" sm="6">
                                <div class="bg-grey-lighten-4 text-subtitle-1 font-weight-medium py-3 px-4 mb-2">
                                    {{ $t('clients') }}
                                </div>
                                <v-data-table
                                    :headers="headers"
                                    :items="clientSummaryQuotationsItems"
                                    disable-sort
                                    hide-default-footer
                                >
                                    <template v-slot:item.turnoverAmount="{item}">
                                        {{ item.turnoverAmount }}
                                        <span v-if="item.turnoverWithouCommissionAmount">
                                            {{ `(${item.turnoverWithouCommissionAmount})` }}
                                        </span>
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-tabs-window-item>
                </v-tabs-window>
            </template>
        </ModalWrapper>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'

    @Component({
        components: {},
    })
    export class ReservationsSummaryModal extends Vue {
        @Prop({default: '', type: String}) filterKey

        modal = false
        isLoading = false

        clientSummary = []
        clientSummaryInCurrency = null
        clientSummaryQuotations = []

        supplierSummary = []
        supplierSummaryInCurrency = null
        supplierSummaryQuotations = []

        tab = 'orders'

        async showModal() {
            try {
                const {
                    clientSummary,
                    clientSummaryInCurrency,
                    clientSummaryQuotations,
                    supplierSummary,
                    supplierSummaryInCurrency,
                    supplierSummaryQuotations,
                } = await this.$api.ordersSummary.get({
                    filterKey: this.filterKey,
                })

                this.clientSummary = clientSummary
                this.clientSummaryInCurrency = clientSummaryInCurrency
                this.clientSummaryQuotations = clientSummaryQuotations

                this.supplierSummary = supplierSummary
                this.supplierSummaryInCurrency = supplierSummaryInCurrency
                this.supplierSummaryQuotations = supplierSummaryQuotations

                this.modal = true
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        get headers() {
            return [
                {title: this.$t('turnovers'), value: 'turnoverAmount', align: 'end'},
                {title: this.$t('due_to_pay'), value: 'dueToPayAmount', align: 'end'},
            ]
        }

        get supplierSummaryItems() {
            return this.supplierSummary.reduce((list, summary) => {
                list.push({
                    dueToPayAmount: this.$filters.priceFormat(summary.dueToPayAmount, summary.currency),
                    turnoverAmount: this.$filters.priceFormat(summary.turnoverAmount, summary.currency),
                })

                return list
            }, [])
        }

        get supplierSummaryQuotationsItems() {
            return this.supplierSummaryQuotations.reduce((list, summary) => {
                list.push({
                    dueToPayAmount: this.$filters.priceFormat(summary.dueToPayAmount, summary.currency),
                    turnoverAmount: this.$filters.priceFormat(summary.turnoverAmount, summary.currency),
                })

                return list
            }, [])
        }

        get clientSummaryItems() {
            return this.clientSummary.reduce((list, summary) => {
                list.push({
                    dueToPayAmount: this.$filters.priceFormat(summary.dueToPayAmount, summary.currency),
                    turnoverAmount: this.$filters.priceFormat(summary.turnoverAmount, summary.currency),
                    ...(summary.turnoverWithouCommissionAmount && {
                        turnoverWithouCommissionAmount: this.$filters.priceFormat(
                            summary.turnoverWithouCommissionAmount,
                            summary.currency
                        ),
                    }),
                })

                return list
            }, [])
        }

        get clientSummaryQuotationsItems() {
            return this.clientSummaryQuotations.reduce((list, summary) => {
                list.push({
                    dueToPayAmount: this.$filters.priceFormat(summary.dueToPayAmount, summary.currency),
                    turnoverAmount: this.$filters.priceFormat(summary.turnoverAmount, summary.currency),
                    ...(summary.turnoverWithouCommissionAmount && {
                        turnoverWithouCommissionAmount: this.$filters.priceFormat(
                            summary.turnoverWithouCommissionAmount,
                            summary.currency
                        ),
                    }),
                })

                return list
            }, [])
        }

        get user() {
            return authStore.user
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default ReservationsSummaryModal
</script>
